import { literal, object } from "./builders";
import { createShapeCaster } from "./casting";
import { Shape } from "./core";

type Encoder<In, Out, InState, OutState> = {
  encode(input: In, state?: InState): Out,
  decode(output: Out, state?: OutState): In,
}
const defineBinaryEncoder = <TIn>(encoder: BinaryEncoder<TIn>) => encoder;
type BinaryEncoder<TIn> = Encoder<TIn, Uint8Array, BinaryEncoderState, BinaryDecoderState>;

class BinaryEncoderState {
  dest: ArrayBuffer | null = null;
  destOffset: number = 0;
}
class BinaryDecoderState {
  offset: number = 0;
}

const simpleBinaryShapeEncoders = {
  boolean: defineBinaryEncoder({
    encode(value: boolean) {
      return new Uint8Array([value ? 1 : 0])
    },
    decode(arr, state = new BinaryDecoderState()) {
      return arr[state.offset] > 1 ? true : false;
    }
  }),
  string: defineBinaryEncoder({
    encode(value: string) {
      const enc = new TextEncoder();
      const stringBytes = enc.encode(value);

      const bytes = new Uint8Array(4 + stringBytes.byteLength);

      const data = new DataView(bytes.buffer);
      data.setInt32(0, stringBytes.byteLength, true);
      bytes.set(stringBytes, 4)

      return bytes;
    },
    decode(arr, state = new BinaryDecoderState()) {
      const data = new DataView(arr.buffer, state.offset);
      const size = data.getUint32(0, true);
      state.offset += 4;

      const denc = new TextDecoder();
      const text = denc.decode(new DataView(arr.buffer, state.offset, size));
      state.offset += size;
      return text;
    }
  }),
  number: defineBinaryEncoder({
    encode(value: number, state = new BinaryEncoderState()) {
      let bytes, floats;

      if (state.dest) {
        bytes = new Uint8Array(state.dest);
        floats = new DataView(state.dest, state.destOffset);
      } else {
        bytes = new Uint8Array(8)
        floats = new DataView(bytes.buffer, 0);
      }
      
      floats.setFloat64(0, value, true)
      
      return bytes;
    },
    decode(bytes, state = new BinaryDecoderState()) {
      const floats = new DataView(bytes.buffer, state.offset, 8);
      state.offset += 8;
      return floats.getFloat64(0, true)
    }
  }),
}

const compoundBinaryShapeEncoders = {
  array(shape: Shape.Compound.Array) {
    const enc = createBinaryShapeEncoder(shape.element);

    return defineBinaryEncoder({
      encode(input: any[], state = new BinaryEncoderState()) {
        const count = input.length;
        const encodedElements = input.map(el => enc.encode(el))

        // sum all the element sizes together
        const size = encodedElements
          .map(el => el.byteLength)
          .reduce((acc, curr) => acc + curr) + 4;
        const bytes = new ArrayBuffer(size);

        // add the count prefix
        const data = new DataView(bytes);
        data.setInt32(0, count);
        const uintArray = new Uint8Array(bytes);

        // write the array data
        let offset = 4;
        
        for (const el of encodedElements) {
          uintArray.set(el, offset);
          offset += el.byteLength;
        }

        return uintArray
      },
      decode(bytes, state = new BinaryDecoderState()) {
        const data = new DataView(bytes.buffer, state.offset);
        const count = data.getInt32(0);
        const elements: any[] = [];
        state.offset += 4;

        for (let i = 0; i < count; i++) {
          elements.push(enc.decode(bytes, state))
        }
        return elements;
      },
    })
  },
  tuple(shape: Shape.Compound.Tuple) {
    const encs = shape.elements.map(createBinaryShapeEncoder);

    return defineBinaryEncoder({
      encode(input: any[]) {
        const encodedElements = input.map((el, i) => encs[i].encode(el))

        const size = encodedElements
          .map(el => el.byteLength)
          .reduce((acc, curr) => acc + curr);

        const bytes = new Uint8Array(size);
        let offset = 0;

        for (const el of encodedElements) {
          bytes.set(el, offset);
          offset += el.byteLength;
        }

        return bytes;
      },
      decode(bytes, state = new BinaryDecoderState()) {
        const elements: any[] = [];

        for (let i = 0; i < shape.elements.length; i++) {
          elements.push(encs[i].decode(bytes, state))
        }

        return elements;
      }
    })
  },
  literal(shape: Shape.Compound.Literal) {
    return defineBinaryEncoder({
      encode(value: Shape.Type.Simple) {
        return new Uint8Array(0);
      },
      decode(arr) {
        return shape.value;
      }
    })
  },
  object(shape: Shape.Compound.Object) {
    const keys = Object.keys(shape.properties)
    const encoders = keys.map(key => createBinaryShapeEncoder(shape.properties[key]));

    return defineBinaryEncoder({
      encode(value: any) {
        const encodedElements = keys.map((key, i) => encoders[i].encode(value[key]))

        const size = encodedElements
          .map(el => el.byteLength)
          .reduce((acc, curr) => acc + curr);

        const bytes = new Uint8Array(size);
        let offset = 0;

        for (const el of encodedElements) {
          bytes.set(el, offset);
          offset += el.byteLength;
        }

        return bytes;
      },
      decode(bytes, state = new BinaryDecoderState()) {
        const propertyEntries: [string, any][] = [];

        for (let i = 0; i < keys.length; i++) {
          propertyEntries.push([keys[i], encoders[i].decode(bytes, state)])
        }

        return Object.fromEntries(propertyEntries);
      }
    })
  }
}

const complexBinaryShapeEncoders = {
  unions(shape: Shape.Complex.Union) {
    const encoders = shape.of.map(createBinaryShapeEncoder);
    const casters = shape.of.map(option => createShapeCaster(option));

    return defineBinaryEncoder({
      encode(input: Shape.Type.Any, state) {
        let caseIndex = -1;

        for (let i = 0; i < casters.length; i++) {
          try {
            casters[i].cast(input)
            caseIndex = i;
          } catch {}
        }
        if (caseIndex === -1)
          throw new Error(`BinaryEncoder could not pick branch in union`);

        const encodedElement = encoders[caseIndex].encode(input)

        // sum all the element sizes together
        const size = encodedElement.byteLength + 4;
        const bytes = new ArrayBuffer(size);

        // add the count prefix
        const data = new DataView(bytes);
        data.setInt32(0, caseIndex);
        const uintArray = new Uint8Array(bytes);
        uintArray.set(encodedElement, 4);

        return uintArray
      },
      decode(bytes, state = new BinaryDecoderState()) {
      
        const data = new DataView(bytes.buffer, state.offset);
        const caseIndex = data.getInt32(0);
        state.offset += 4;
        
        return encoders[caseIndex].decode(bytes, state);
      },
    })
  }
}

export const createBinaryShapeEncoder = <T extends Shape.Any>(shape: T): BinaryEncoder<Shape.Type.Of<T>> => {
  // we need to cheat because typescript wont validate our generic refinement
  type AnEncoder = BinaryEncoder<any>;

  switch (shape.type) {
    case 'boolean':
      return simpleBinaryShapeEncoders.boolean as AnEncoder;
    case 'string':
      return simpleBinaryShapeEncoders.string as AnEncoder;
    case 'number':
      return simpleBinaryShapeEncoders.number as AnEncoder;
    case 'unknown':
      throw new Error(`Can't encode an unknown shape`)
    case 'array':
      return compoundBinaryShapeEncoders.array(shape) as AnEncoder;
    case 'tuple':
      return compoundBinaryShapeEncoders.tuple(shape) as AnEncoder;
    case 'literal':
      return compoundBinaryShapeEncoders.literal(shape) as AnEncoder;
    case 'object':
      return compoundBinaryShapeEncoders.object(shape) as AnEncoder;
    case 'ref':
      return createBinaryShapeEncoder(shape.real) as AnEncoder;
    case 'union':
      return complexBinaryShapeEncoders.unions(shape) as AnEncoder;
    
    default:
      throw new Error(`Binary Encoding not yet supported for type: "${(shape as Shape.Any).type}"`)
  }
};

export const Binary = {
  createBinaryShapeEncoder,
  packBytes(...bytes: [number, number, number, number]) {
    let result = 0;

    // bitwise operations in JS work as if they were 32bit ints
    const BYTE_MASK = 0x000000FF;

    for (let i = 0; i < 4; i++) {
      // mask out non-byte bits
      let byte = bytes[i] & BYTE_MASK;
      // shift byte into position
      byte <<= 8 * i;

      // write byte into result
      result |= byte;
    }

    return result;
  },
  unpackBytes(packedBytesNumber: number) {
    const bytes: [number, number, number, number] = [0, 0, 0, 0];
    const BYTE_MASK = 0x000000FF;

    for (let i = 0; i < 4; i++) {
      // shift byte into position
      let byte = packedBytesNumber >> 8 * i;
      // mask out bits from other bytes
      byte &= BYTE_MASK;

      // write byte into result
      bytes[i] = byte;
    }
    
    return bytes;
  },
  writePackedByte(packedValue: number, byteIndex: 0 | 1 | 2 | 3, valueToWrite: number) {
    const BYTE_MASK = 0x000000FF;

    let output = packedValue;
    let byte = valueToWrite;
    
    // mask out non-byte parts
    byte &= BYTE_MASK;
    // shift to the right position
    byte <<= 8 * byteIndex;

    // clear packedValue existing bytes
    output &= ~(BYTE_MASK << (8 * byteIndex))
    // write the byte
    output |= byte;

    return output;
  }
}
